import { useSearchParams } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardBody,
  MetricCard,
  InsightCard,
  DataTable,
  Chart,
  Briefing,
  DashboardSkeleton,
  PageError,
  SampleDataBanner,
} from "../components/index.js";
import { useDashboardData } from "../hooks/useDashboardData.js";
import { rangeDays } from "../lib/time.js";

export function Sales() {
  const { data, loading, error, refetch } = useDashboardData("sales");
  const [searchParams] = useSearchParams();
  const days = rangeDays(searchParams.get("from"), searchParams.get("to"));

  if (loading) return <DashboardSkeleton />;
  if (error)   return <PageError error={error} onRetry={refetch} />;
  if (!data)   return null;

  const {
    metrics,
    channelBreakdown,
    revenueByDay,
    topProducts,
    territory,
    expansionOpportunities,
    buyerInsights,
    preCallBriefing,
    insights,
    period,
    clientContext,
  } = data;
  const isEcommerce = clientContext?.isEcommerce;

  return (
    <div className="space-y-5 max-w-[1400px]">
      {period?.hasRealData === false && <SampleDataBanner />}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard {...metrics.totalOrders} />
        <MetricCard {...metrics.totalRevenue} />
        <MetricCard {...metrics.avgOrderValue} />
        {/*
          Fourth card swaps by business type. Ecommerce clients see dollar-
          weighted recoverable pipeline (Online Opportunity) — a concrete
          sales-rep goal. Service businesses keep Daily Leads unchanged.
        */}
        {isEcommerce
          ? <MetricCard {...metrics.onlineOpportunity} />
          : <MetricCard {...metrics.dailyLeads} />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader
            title="Channel Breakdown"
            subtitle={`All sales channels · ${days} days`}
          />
          <DataTable
            columns={[
              { key: "channel", label: "Channel" },
              { key: "orders",  label: "Orders",  align: "right",
                render: (r) => r.orders.toLocaleString() },
              { key: "revenue", label: "Revenue", align: "right",
                render: (r) => `$${r.revenue.toLocaleString()}` },
              { key: "aov",     label: "AOV",     align: "right",
                render: (r) => `$${r.aov.toFixed(2)}` },
              { key: "pct",     label: "Share",   align: "right",
                render: (r) => `${r.pct}%` },
            ]}
            rows={channelBreakdown}
            highlightFn={(r) => r.channel === "Online Store"}
          />
        </Card>

        <Card>
          <CardHeader title="Revenue by Day" subtitle="Weekly shape" />
          <CardBody>
            <Chart
              type="bar"
              data={revenueByDay}
              xKey="day"
              series={[{ key: "revenue", color: "#ff6804", label: "Revenue" }]}
              height={220}
              yFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
            />
          </CardBody>
        </Card>
      </div>

      {insights?.[0] && <InsightCard>{insights[0].content}</InsightCard>}

      <Card>
        <CardHeader title="Top Products" subtitle={`Revenue drivers · ${days} days`} />
        <DataTable
          columns={[
            { key: "product", label: "Product" },
            { key: "qty",     label: "Units",   align: "right",
              render: (r) => r.qty.toLocaleString() },
            { key: "revenue",label: "Revenue", align: "right",
              render: (r) => `$${r.revenue.toLocaleString()}` },
            { key: "channel", label: "Primary Channel" },
          ]}
          rows={topProducts}
        />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          {/* Territory split is data-driven (top-visits city + ad-reached
              cities) — label the card from the data, never a hardcoded metro. */}
          <CardHeader
            title="Home Territory"
            subtitle={territory?.[0] ? `${territory[0].city} · core market` : "Core market"}
          />
          <DataTable
            columns={[
              { key: "city",     label: "City" },
              { key: "visits",   label: "Visits",    align: "right",
                render: (r) => r.visits.toLocaleString() },
              { key: "adClicks", label: "Ad Clicks", align: "right" },
            ]}
            rows={territory}
          />
        </Card>

        <Card>
          <CardHeader title="Expansion Opportunities" subtitle="Organic pull, no paid push" />
          <DataTable
            columns={[
              { key: "city",   label: "City" },
              { key: "visits", label: "Visits", align: "right",
                render: (r) => r.visits.toLocaleString() },
              { key: "note",   label: "Analysis" },
            ]}
            rows={expansionOpportunities}
            highlightFn={() => true}
          />
        </Card>
      </div>

      {insights?.[1] && <InsightCard>{insights[1].content}</InsightCard>}

      {/* Structured buyer-behavior cards + the pre-call briefing are only
          rendered when they exist — an empty payload hides the sections
          entirely rather than showing hollow cards. */}
      {buyerInsights?.length > 0 && (
        <Card>
          <CardHeader title="Buyer Behavior" subtitle="Signals from the data" />
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {buyerInsights.map((b) => (
                <div
                  key={b.title}
                  className="border border-border-light rounded-card p-4"
                >
                  <div className="text-[12px] font-semibold text-text-primary mb-1.5">
                    {b.title}
                  </div>
                  <div className="text-[11px] text-text-secondary leading-relaxed">
                    {b.body}
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      <Briefing title="Pre-Call Briefing" items={preCallBriefing} />
    </div>
  );
}
